import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { useConsolidatedNotifications } from '@/contexts/ConsolidatedNotificationContext';
import { useToast } from '@/hooks/use-toast';
import { useVisualNotifications } from '@/hooks/notifications/useVisualNotifications';

interface NotificationConnection {
  isConnected: boolean;
  reconnectAttempts: number;
  lastReconnectTime: number;
}

export const useReliableNotifications = () => {
  const { user, isSuperAdmin, hasAdminAccess } = useOptimizedAuth();
  const { hasPermission } = useConsolidatedNotifications();
  const { toast } = useToast();
  const visualNotifications = useVisualNotifications();
  
  const [connectionStatus, setConnectionStatus] = useState<NotificationConnection>({
    isConnected: false,
    reconnectAttempts: 0,
    lastReconnectTime: 0
  });

  const channelRef = useRef<any>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 2000;

  const connectToNotifications = () => {
    if (!user || channelRef.current) return;

    // Connecting to reliable notifications

    const channel = supabase
      .channel('reliable-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: (isSuperAdmin || hasAdminAccess) ? undefined : `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Real-time notification received
          handleIncomingNotification(payload.new);
        }
      )
      .subscribe((status) => {
        // Notification subscription status
        
        if (status === 'SUBSCRIBED') {
          setConnectionStatus(prev => ({
            ...prev,
            isConnected: true,
            reconnectAttempts: 0
          }));
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setConnectionStatus(prev => ({
            ...prev,
            isConnected: false
          }));
          handleReconnection();
        }
      });

    channelRef.current = channel;
  };

  const handleIncomingNotification = (notification: any) => {
    // Only show for relevant users
    if (!isSuperAdmin && !hasAdminAccess && notification.user_id !== user?.id) {
      return;
    }

    // Show toast notification for high priority items
    if (notification.priority === 'high' || notification.priority === 'urgent') {
      toast({
        title: notification.title,
        description: notification.message,
        duration: notification.priority === 'urgent' ? 8000 : 5000,
        variant: notification.priority === 'urgent' ? 'destructive' : 'default'
      });
    }

    // Show visual notification with enhanced features
    visualNotifications.showNotification({
      type: getPriorityType(notification.priority),
      title: notification.title,
      message: notification.message,
      priority: notification.priority,
      persistent: notification.priority === 'urgent'
    });

    // Show browser notification if permission granted and tab not focused
    if (hasPermission && document.hidden) {
      try {
        const browserNotification = new Notification(notification.title, {
          body: notification.message,
          icon: notification.image_url || '/favicon.ico',
          tag: notification.id,
          requireInteraction: notification.priority === 'urgent'
        });

        // Auto-close non-urgent notifications
        if (notification.priority !== 'urgent') {
          setTimeout(() => browserNotification.close(), 5000);
        }
      } catch (error) {
        console.error('Failed to show browser notification:', error);
      }
    }

    // Play sound for sound-enabled notifications
    if (notification.priority === 'urgent' || notification.priority === 'high') {
      playNotificationSound(notification.priority);
    }
  };

  const getPriorityType = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'low': return 'info';
      default: return 'success';
    }
  };

  const playNotificationSound = (priority: string) => {
    // Create audio context for sound notifications
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different tones for different priorities
      oscillator.frequency.value = priority === 'urgent' ? 800 : 400;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  };

  const handleReconnection = () => {
    if (connectionStatus.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('🔔 Max reconnection attempts reached');
      return;
    }

    const delay = RECONNECT_DELAY * Math.pow(2, connectionStatus.reconnectAttempts);
    
    // Reconnecting notification service
    
    reconnectTimeoutRef.current = setTimeout(() => {
      setConnectionStatus(prev => ({
        ...prev,
        reconnectAttempts: prev.reconnectAttempts + 1,
        lastReconnectTime: Date.now()
      }));
      
      disconnect();
      connectToNotifications();
    }, delay);
  };

  const disconnect = () => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    
    setConnectionStatus(prev => ({ ...prev, isConnected: false }));
  };

  useEffect(() => {
    if (user) {
      connectToNotifications();
    }

    return () => {
      disconnect();
    };
  }, [user?.id, isSuperAdmin, hasAdminAccess]);

  // Monitor connection health
  useEffect(() => {
    const interval = setInterval(() => {
      if (user && !connectionStatus.isConnected && connectionStatus.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        // Health check: Attempting to reconnect
        handleReconnection();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [user, connectionStatus.isConnected, connectionStatus.reconnectAttempts]);

  return {
    isConnected: connectionStatus.isConnected,
    reconnectAttempts: connectionStatus.reconnectAttempts,
    forceReconnect: () => {
      disconnect();
      setConnectionStatus(prev => ({ ...prev, reconnectAttempts: 0 }));
      connectToNotifications();
    }
  };
};